import PercentIcon from '@mui/icons-material/Percent'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { MainApi } from 'api/MainApi'
import { MainService } from 'api/schema'
import clsx from 'clsx'
import GausiumTaskReportView from 'components/GausiumTaskReportView/GausiumTaskReportView'
import {
  gausiumReportParamsFromRow,
  isGausiumTaskReportRow,
} from 'components/GausiumTaskReportView/gausiumTaskReportUtils'
import Loader from 'components/Loader/Loader'
import TablePagination from 'components/TablePagination/TablePagination'
import { parseISO } from 'date-fns'
import {
  fetchNormalizedHistoryForRobotSerials,
  fetchNormalizedHistoryMergedWithAutonomicsSnapshot,
  getDefaultHistoryRange,
  refreshGausiumNormalizedHistory,
} from 'features/tasksNormalized/fetchNormalizedHistory'
import type { NormalizedStatisticsApiRow } from 'features/tasksNormalized/filterableRecord'
import {
  getNormalizedExecutionStatusText,
  renderNormalizedExecutionStatusIcon,
} from 'features/tasksNormalized/formatNormalizedExecutionStatus'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GAUSIUM_ENABLED, PATH, PROGRESS_TYPES } from 'utils/constants'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './NormalizedMissionHistoryPanel.module.css'
import reportStyle from 'components/ReportViewActions/ReportViewActions.module.css'

const DEFAULT_PAGE_SIZE = 20
const ESTIMATED_ROW_HEIGHT_PX = 52
const RESERVED_VERTICAL_GAP_PX = 16

function completionToPercent(raw: number): number {
  if (!Number.isFinite(raw)) return raw
  if (raw >= 0 && raw <= 1) return raw * 100
  return raw
}

function deriveCompletionPercent(row: NormalizedStatisticsApiRow): number {
  const explicitCompletionRaw = row.completion_percentage
  if (explicitCompletionRaw != null && Number.isFinite(Number(explicitCompletionRaw))) {
    const pct = completionToPercent(Number(explicitCompletionRaw))
    return Math.max(0, Math.min(100, pct))
  }

  const actual = Number(row.travel_distance_actual)
  const planned = Number(row.travel_distance_planned)
  if (Number.isFinite(actual) && Number.isFinite(planned) && planned > 0) {
    const pct = (actual / planned) * 100
    return Math.max(0, Math.min(100, pct))
  }

  return Number.NaN
}

function parseNumericId(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value.trim())
  return null
}

export type LinkScenarioMissionMode = boolean | 'gausium_only'

function resolveLinkScenarioMissionToPages(
  mode: LinkScenarioMissionMode | undefined,
  row: NormalizedStatisticsApiRow
): boolean {
  if (mode === false) return false
  if (mode === true) return true
  if (mode === 'gausium_only') {
    return row.vendor_origin_record_type === 'gausium_task_report' || row.vendor === VENDOR.GAUSIUM
  }
  return true
}

export type NormalizedMissionHistoryPanelProps = {
  robotSerialNumbers: string[]
  showRobotColumn?: boolean
  showStatusColumn?: boolean
  /** Right column: report icon → fetch PNG URL, show in dialog (Gausium task-report rows only). */
  showGausiumReportActions?: boolean
  onRowClick?: (row: NormalizedStatisticsApiRow) => void
  /**
   * Scenario/mission column links to scenario/mission pages.
   * - `false`: plain text (Robot Autonomics History).
   * - `true`: links for every row.
   * - `'gausium_only'`: links only for GS task-report rows (Facility History with mixed robots).
   */
  linkScenarioMissionToPages?: LinkScenarioMissionMode
  displayOffsetMinutes?: number
}

function safeParse(s: string | undefined | null): Date | null {
  if (!s) return null
  try {
    const d = parseISO(String(s).replace(' ', 'T'))
    return Number.isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

function sortRows(rows: NormalizedStatisticsApiRow[]): NormalizedStatisticsApiRow[] {
  return [...rows].sort((a, b) => {
    const ta = safeParse(a.start_time || a.created_at)?.getTime() ?? 0
    const tb = safeParse(b.start_time || b.created_at)?.getTime() ?? 0
    return tb - ta
  })
}

/** Autonomics: numeric scenario PK lives in `scenario_or_map_id` (same column as GS map name string). */
function resolveScenarioNumericId(row: NormalizedStatisticsApiRow): number | null {
  const isGausium = row.vendor_origin_record_type === 'gausium_task_report' || row.vendor === VENDOR.GAUSIUM
  if (isGausium) return null
  const som = row.scenario_or_map_id
  if (typeof som === 'number' && Number.isFinite(som)) return som
  if (typeof som === 'string' && /^\d+$/.test(som.trim())) return Number(som.trim())
  return null
}

/** One label: scenario name if scenario id exists; else mission name (or map string for GS). */
function ScenarioOrMissionPrimaryLabel({
  row,
  scenarioNames,
  missionNames,
  linkScenarioMissionToPages,
}: {
  row: NormalizedStatisticsApiRow
  scenarioNames: Record<number, string>
  missionNames: Record<number, string>
  linkScenarioMissionToPages: boolean
}) {
  const isGausiumRow = row.vendor_origin_record_type === 'gausium_task_report' || row.vendor === VENDOR.GAUSIUM
  if (isGausiumRow) {
    const taskName = String(row.name || '').trim()
    const som = row.scenario_or_map_id
    const mapNameRaw = som != null ? String(som).trim() : ''
    const mapName = mapNameRaw && mapNameRaw !== taskName ? mapNameRaw : ''
    const mapId = parseNumericId(som)
    const mapFallback = !mapName && mapId != null ? `Map ${mapId}` : ''

    if (taskName && (mapName || mapFallback)) {
      return <span>{`${taskName} / ${mapName || mapFallback}`}</span>
    }
    if (taskName) return <span>{taskName}</span>
    if (mapName || mapFallback) return <span>{mapName || mapFallback}</span>
    return <span>—</span>
  }

  const scenarioId = resolveScenarioNumericId(row)
  if (scenarioId != null) {
    const label = scenarioNames[scenarioId]?.trim() || `S${scenarioId}`
    return linkScenarioMissionToPages ? (
      <a className={style.link} href={`${PATH.SCENARIOS}${scenarioId}`} target="_blank" rel="noreferrer">
        {label}
      </a>
    ) : (
      <span>{label}</span>
    )
  }

  const missionsInfo = row.missions_info ?? []
  for (let i = 0; i < missionsInfo.length; i++) {
    const m = missionsInfo[i]
    if (!m || typeof m !== 'object') continue
    const rawId = m.id
    const mname = (m.name != null ? String(m.name) : '').trim()
    if (mname) {
      if (typeof rawId === 'number' && Number.isFinite(rawId)) {
        return linkScenarioMissionToPages ? (
          <a key={`m-${i}`} className={style.link} href={`${PATH.MISSION}${rawId}`} target="_blank" rel="noreferrer">
            {mname}
          </a>
        ) : (
          <span key={`m-${i}`}>{mname}</span>
        )
      }
      const s = String(rawId ?? '').trim()
      if (/^\d+$/.test(s)) {
        const num = Number(s)
        return linkScenarioMissionToPages ? (
          <a key={`m-${i}`} className={style.link} href={`${PATH.MISSION}${num}`} target="_blank" rel="noreferrer">
            {mname}
          </a>
        ) : (
          <span key={`m-${i}`}>{mname}</span>
        )
      }
      return <span key={`m-${i}`}>{mname}</span>
    }
  }

  const mid = row.mission_or_area_id
  let missionNum: number | null = null
  if (typeof mid === 'number' && Number.isFinite(mid)) missionNum = mid
  else if (typeof mid === 'string' && /^\d+$/.test(mid.trim())) missionNum = Number(mid.trim())
  if (missionNum != null) {
    const label = missionNames[missionNum]?.trim() || `M${missionNum}`
    return linkScenarioMissionToPages ? (
      <a className={style.link} href={`${PATH.MISSION}${missionNum}`} target="_blank" rel="noreferrer">
        {label}
      </a>
    ) : (
      <span>{label}</span>
    )
  }

  const som = row.scenario_or_map_id
  const gsMapId = parseNumericId(som)
  if (isGausiumRow && gsMapId != null) return <span>{`Map ${gsMapId}`}</span>
  if (som != null && String(som).trim() !== '') {
    return <span>{String(som)}</span>
  }

  return <span>—</span>
}

const NormalizedMissionHistoryPanel = ({
  robotSerialNumbers,
  showRobotColumn,
  showStatusColumn = true,
  showGausiumReportActions = false,
  onRowClick,
  linkScenarioMissionToPages = true,
  displayOffsetMinutes,
}: NormalizedMissionHistoryPanelProps) => {
  const browserOffsetMinutes = -new Date().getTimezoneOffset()
  const shiftMs =
    (displayOffsetMinutes ?? browserOffsetMinutes) !== browserOffsetMinutes
      ? (displayOffsetMinutes! - browserOffsetMinutes) * 60_000
      : 0

  const { t } = useTranslation()
  const [progress, setProgress] = useState(PROGRESS_TYPES.IDLE)
  const [rows, setRows] = useState<NormalizedStatisticsApiRow[]>([])
  const [reportFeedback, setReportFeedback] = useState<string | null>(null)
  const reportFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showReportError = (message: string) => {
    if (reportFeedbackTimerRef.current) {
      clearTimeout(reportFeedbackTimerRef.current)
      reportFeedbackTimerRef.current = null
    }
    setReportFeedback(message)
    reportFeedbackTimerRef.current = setTimeout(() => {
      setReportFeedback(null)
      reportFeedbackTimerRef.current = null
    }, 10000)
  }

  useEffect(() => {
    return () => {
      if (reportFeedbackTimerRef.current) clearTimeout(reportFeedbackTimerRef.current)
    }
  }, [])
  const [scenarioNameById, setScenarioNameById] = useState<Record<number, string>>({})
  const [missionNameById, setMissionNameById] = useState<Record<number, string>>({})
  const [page, setPage] = useState(1)
  const [pageInput, setPageInput] = useState('1')
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const rootRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const paginationRef = useRef<HTMLDivElement>(null)
  const firstRowRef = useRef<HTMLDivElement>(null)

  const snsKey = useMemo(
    () =>
      Array.from(new Set(robotSerialNumbers.map(s => String(s).trim()).filter(Boolean)))
        .sort()
        .join('|'),
    [robotSerialNumbers]
  )

  useEffect(() => {
    if (!snsKey) {
      setRows([])
      setProgress(PROGRESS_TYPES.SUCCESS)
      return
    }

    let cancelled = false
    const { start, end } = getDefaultHistoryRange()
    const sns = snsKey.split('|')

    const run = async () => {
      setProgress(PROGRESS_TYPES.WORK)
      try {
        if (sns.length === 1) {
          const sn = sns[0]
          const { merged, autonomicsRows } = await fetchNormalizedHistoryMergedWithAutonomicsSnapshot({
            start,
            end,
            robotSn: sn,
          })
          if (cancelled) return
          setRows(sortRows(merged))
          setProgress(PROGRESS_TYPES.SUCCESS)

          if (GAUSIUM_ENABLED) {
            refreshGausiumNormalizedHistory({
              start,
              end,
              robotSn: sn,
              autonomicsSnapshot: autonomicsRows,
            })
              .then(next => {
                if (!cancelled) setRows(sortRows(next))
              })
              .catch(() => undefined)
          }
        } else {
          const merged = await fetchNormalizedHistoryForRobotSerials(sns, start, end)
          if (cancelled) return
          setRows(sortRows(merged))
          setProgress(PROGRESS_TYPES.SUCCESS)
        }
      } catch {
        if (!cancelled) {
          setRows([])
          setProgress(PROGRESS_TYPES.ERROR)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [snsKey])

  useEffect(() => {
    setPage(1)
  }, [snsKey])

  useEffect(() => {
    let cancelled = false
    Promise.all([MainApi.fetchScenarios(1, 3000), MainService.mainMissionList(['-id'], 1, 3000)])
      .then(([scenRes, missRes]) => {
        if (cancelled) return
        const scenarios: Record<number, string> = {}
        const missions: Record<number, string> = {}
        scenRes.results?.forEach((s: { id?: number; name?: string }) => {
          if (typeof s.id === 'number' && typeof s.name === 'string') scenarios[s.id] = s.name
        })
        missRes.results?.forEach((m: { id?: number; name?: string | null }) => {
          if (typeof m.id === 'number' && typeof m.name === 'string') missions[m.id] = m.name
        })
        setScenarioNameById(scenarios)
        setMissionNameById(missions)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const measurePageSize = () => {
      const rootHeight = rootRef.current?.clientHeight ?? 0
      if (rootHeight <= 0) return
      const headerHeight = headerRef.current?.offsetHeight ?? ESTIMATED_ROW_HEIGHT_PX
      const paginationHeight = rows.length > 0 ? paginationRef.current?.offsetHeight ?? 52 : 0
      const measuredRowHeight = firstRowRef.current?.offsetHeight ?? ESTIMATED_ROW_HEIGHT_PX
      const availableRowsHeight = rootHeight - headerHeight - paginationHeight - RESERVED_VERTICAL_GAP_PX
      const nextPageSize = Math.max(1, Math.floor(availableRowsHeight / Math.max(1, measuredRowHeight)))
      setPageSize(prev => (prev === nextPageSize ? prev : nextPageSize))
    }

    measurePageSize()

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measurePageSize) : null
    if (observer && rootRef.current) observer.observe(rootRef.current)
    window.addEventListener('resize', measurePageSize)

    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('resize', measurePageSize)
    }
  }, [rows.length, showRobotColumn, showGausiumReportActions, page])

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [rows.length, totalPages, page])

  useEffect(() => {
    setPageInput(String(page))
  }, [page])

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize
    return rows.slice(start, start + pageSize)
  }, [rows, page, pageSize])

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput, 10)
      if (!Number.isNaN(val) && val >= 1 && val <= totalPages) {
        setPage(val)
      } else {
        setPageInput(String(page))
      }
    }
  }

  const gridClass = showRobotColumn ? style.withRobot : undefined
  const isOnlyGausiumRows = rows.length > 0 && rows.every(row => isGausiumTaskReportRow(row.vendor_origin_record_type))
  const scenarioMissionHeaderLabel = isOnlyGausiumRows
    ? t('robot.history_col_task_map', 'Task / Map')
    : t('robot.history_col_scenario_mission', 'Scenario / Mission')
  const actionsCol = showGausiumReportActions ? ' minmax(82px, 0.65fr)' : ''
  const gridTemplateColumns = (() => {
    const parts = []
    parts.push(showRobotColumn ? '1.3fr' : '1.6fr') // Start
    if (showRobotColumn) parts.push('1.8fr') // Serial ID

    // Scenario gets the bulk of the space
    if (showRobotColumn) {
      parts.push(showStatusColumn ? '3.0fr' : '3.8fr')
    } else {
      parts.push(showStatusColumn ? '3.8fr' : '4.6fr')
    }

    parts.push('0.8fr') // Completion (Always)
    if (showStatusColumn) parts.push('0.8fr') // Status

    let res = parts.join(' ')
    if (showGausiumReportActions) res += actionsCol
    return res
  })()

  if (progress === PROGRESS_TYPES.WORK) {
    return (
      <div className={style.root}>
        <Loader />
      </div>
    )
  }

  if (progress === PROGRESS_TYPES.ERROR) {
    return <div className={style.error}>{t('robot.history_load_error', 'Failed to load history')}</div>
  }

  if (!snsKey) {
    return <div className={style.empty}>{t('robot.history_no_robot', 'No robot selected')}</div>
  }

  const headerRow = (
    <div ref={headerRef} className={clsx(style.tableHeader, gridClass)} style={{ gridTemplateColumns }}>
      <div className={style.tableHeaderItem}>{t('robot.history_col_start', 'Start')}</div>
      {showRobotColumn && <div className={style.tableHeaderItem}>{t('robots.serial_id', 'Serial ID')}</div>}
      <div className={style.tableHeaderItem}>{scenarioMissionHeaderLabel}</div>
      <div
        className={style.tableHeaderItem}
        style={{ display: 'flex', justifyContent: 'center' }}
        title={t('robot.history_col_completion', 'Completion')}
      >
        <PercentIcon fontSize="small" />
      </div>
      {showStatusColumn && (
        <div
          className={style.tableHeaderItem}
          style={{ display: 'flex', justifyContent: 'center' }}
          title={t('robot.history_col_status', 'Status')}
        >
          <TaskAltIcon fontSize="small" color="success" />
        </div>
      )}
      {showGausiumReportActions && <div className={style.tableHeaderItem}>{t('common.reports', 'Reports')}</div>}
    </div>
  )

  const rowMap = pagedRows.map((row, idx) => {
    const rawStart = safeParse(row.start_time || row.created_at)
    const startAt = rawStart ? (shiftMs !== 0 ? new Date(rawStart.getTime() + shiftMs) : rawStart) : null
    const completionPercent = deriveCompletionPercent(row)
    const completionLabel = Number.isFinite(completionPercent) ? `${Math.round(completionPercent)}%` : '—'
    const executionStatusIcon = renderNormalizedExecutionStatusIcon(row)
    const executionStatusText = getNormalizedExecutionStatusText(row, t)
    const statusTitle = executionStatusText
    const rowClass = clsx(style.tableRow, gridClass)
    const showReportAction = showGausiumReportActions && isGausiumTaskReportRow(row.vendor_origin_record_type)
    const reportParams = showReportAction ? gausiumReportParamsFromRow(row) : null
    const startCellClass = clsx(style.tableColWrap, style.startCell)
    const scenarioCellClass = style.tableColWrap
    const scenarioCellStyle = isGausiumTaskReportRow(row.vendor_origin_record_type)
      ? {
          whiteSpace: 'normal' as const,
          wordBreak: 'break-word' as const,
          overflow: 'visible' as const,
          textOverflow: 'clip' as const,
        }
      : undefined
    const completionCellClass = style.tableCol
    const executionStatusCellClass = style.tableCol

    return (
      <div
        key={`${row.vendor_origin_record_type ?? ''}:${row.event_id}`}
        ref={idx === 0 ? firstRowRef : undefined}
        className={rowClass}
        style={{ gridTemplateColumns }}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        role={onRowClick ? 'button' : undefined}
        tabIndex={onRowClick ? 0 : undefined}
        onKeyDown={
          onRowClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onRowClick(row)
                }
              }
            : undefined
        }
      >
        <div className={startCellClass}>
          {startAt ? (
            <>
              {startAt.toLocaleDateString()}
              <br />
              {startAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
            </>
          ) : (
            '—'
          )}
        </div>
        {showRobotColumn && <div className={style.tableColWrap}>{row.robot_sn || '—'}</div>}
        <div className={scenarioCellClass} style={scenarioCellStyle}>
          <ScenarioOrMissionPrimaryLabel
            row={row}
            scenarioNames={scenarioNameById}
            missionNames={missionNameById}
            linkScenarioMissionToPages={resolveLinkScenarioMissionToPages(linkScenarioMissionToPages, row)}
          />
          {Boolean(row.cancellation_reason) && Number(row.execution_status) !== 8 && (
            <div
              style={{
                fontSize: '0.75rem',
                color: 'var(--text-secondary, #718096)',
                marginTop: '2px',
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}
            >
              <span style={{ fontWeight: 'bold' }}>{t('common.reason', 'Reason')}: </span>
              {String(row.cancellation_reason).replace(/^(the task was not started:\s*|reason:\s*)/i, '')}
            </div>
          )}
        </div>
        <div
          className={completionCellClass}
          style={{ display: 'flex', justifyContent: 'center' }}
          title={statusTitle || undefined}
        >
          {completionLabel}
        </div>
        {showStatusColumn && (
          <div
            className={executionStatusCellClass}
            style={{ display: 'flex', justifyContent: 'center' }}
            title={executionStatusText}
          >
            {executionStatusIcon}
          </div>
        )}
        {showGausiumReportActions && (
          <div className={style.actionsCell}>
            {reportParams?.robotSn && reportParams.reportId ? (
              <GausiumTaskReportView
                robotSn={reportParams.robotSn}
                reportId={reportParams.reportId}
                startAt={reportParams.startAt}
                onError={showReportError}
              />
            ) : null}
          </div>
        )}
      </div>
    )
  })

  return (
    <div ref={rootRef} className={style.root}>
      {reportFeedback && (
        <div className={reportStyle.feedback} role="alert">
          <span>{reportFeedback}</span>
          <button
            type="button"
            className={reportStyle.feedbackDismiss}
            onClick={() => {
              if (reportFeedbackTimerRef.current) {
                clearTimeout(reportFeedbackTimerRef.current)
                reportFeedbackTimerRef.current = null
              }
              setReportFeedback(null)
            }}
            aria-label={t('common.hide', 'Hide')}
          >
            ×
          </button>
        </div>
      )}
      <div className={style.tableWrapper}>
        {headerRow}
        {rows.length === 0 ? (
          <div className={style.empty}>{t('robot.history_empty', 'No missions in the last 90 days')}</div>
        ) : (
          <div className={style.table}>{rowMap}</div>
        )}
      </div>
      {rows.length > 0 && (
        <div ref={paginationRef}>
          <TablePagination
            page={page}
            totalPages={totalPages}
            pageInput={pageInput}
            onPageInputChange={setPageInput}
            onPageInputKeyDown={handlePageJump}
            onPageInputBlur={() => setPageInput(String(page))}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => Math.min(totalPages, p + 1))}
          />
        </div>
      )}
    </div>
  )
}

export default NormalizedMissionHistoryPanel
